// /languages — enable/disable storefront languages + translation coverage.
//
// Two parts:
//  1. Enabled languages — LV is always on; EN/RU can be switched on/off. The
//     storefront reads this to decide which languages to offer in its toggle
//     (and hides the toggle entirely when only one is enabled).
//  2. Coverage — how much of the catalog (and UI strings) is still untranslated
//     per language, with a searchable, paginated drill-down of the missing rows.

import { useCallback, useEffect, useState } from "react";
import { Globe, Languages as LanguagesIcon, Search as SearchIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { TranslationsTable } from "./TranslationsTable";

type Locale = "lv" | "en" | "ru";
type TargetLocale = "en" | "ru";
const LOCALE_LABEL: Record<Locale, string> = { lv: "Latviešu", en: "English", ru: "Русский" };

type SettingsResp = { enabledLocales: Locale[]; allLocales: Locale[] };
type Missing = { en: number; ru: number };
type CoverageResp = {
  locales: TargetLocale[];
  content: Record<string, { total: number; missing: Missing }>;
  ui: { keys: number; missing: Missing };
};
type UntranslatedResp = {
  items: Array<{ id: string; slug: string; name_lv: string | null }>;
  total: number;
  limit: number;
  offset: number;
};

const CONTENT_TYPES: Array<{ key: string; labelKey: string; fallback: string }> = [
  { key: "products", labelKey: "languages.products", fallback: "Products" },
  { key: "categories", labelKey: "languages.categories", fallback: "Categories" },
  { key: "tags", labelKey: "languages.tags", fallback: "Tags" },
  { key: "grapes", labelKey: "languages.grapes", fallback: "Grape varieties" },
];

const PAGE_SIZE = 25;

export function Languages() {
  const { toast } = useToast();
  const t = useT();

  const [settings, setSettings] = useState<SettingsResp | null>(null);
  const [coverage, setCoverage] = useState<CoverageResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingLocale, setSavingLocale] = useState<Locale | null>(null);

  // Drill-down dialog state.
  const [drill, setDrill] = useState<{ type: string; label: string; locale: TargetLocale } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api<SettingsResp>("/api/v1/admin/i18n/settings"),
        api<CoverageResp>("/api/v1/admin/i18n/coverage"),
      ]);
      setSettings(s);
      setCoverage(c);
    } catch (err) {
      toast({
        title: t("languages.loadError", "Failed to load languages"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleLocale = async (locale: TargetLocale, on: boolean) => {
    if (!settings) return;
    const next = on
      ? [...new Set([...settings.enabledLocales, locale])]
      : settings.enabledLocales.filter((l) => l !== locale);
    setSavingLocale(locale);
    try {
      const resp = await api<{ enabledLocales: Locale[] }>("/api/v1/admin/i18n/settings", {
        method: "PUT",
        body: JSON.stringify({ enabledLocales: next }),
      });
      setSettings((prev) => (prev ? { ...prev, enabledLocales: resp.enabledLocales } : prev));
      toast({
        title: t("languages.saved", "Languages updated"),
        description: t("languages.savedHint", "Changes appear on the storefront within a minute."),
      });
    } catch (err) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setSavingLocale(null);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center gap-3">
        <LanguagesIcon className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">
            {t("languages.title", "Languages")}
          </h1>
          <p className="font-body text-sm text-muted-foreground">
            {t(
              "languages.subtitle",
              "Turn storefront languages on or off and see what's still untranslated.",
            )}
          </p>
        </div>
      </div>

      {/* ── Enabled languages ── */}
      <section className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h2 className="font-display text-lg font-semibold text-foreground flex items-center gap-2">
          <Globe className="h-4 w-4" /> {t("languages.enabledTitle", "Enabled languages")}
        </h2>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "languages.enabledHint",
            "Latvian is always on. Enable a language once its content is translated — the storefront hides the switcher when only one language is on.",
          )}
        </p>
        <div className="divide-y divide-border">
          {(["lv", "en", "ru"] as Locale[]).map((locale) => {
            const on = settings?.enabledLocales.includes(locale) ?? locale === "lv";
            return (
              <div key={locale} className="flex items-center justify-between py-3">
                <div>
                  <div className="font-body text-sm font-medium text-foreground">
                    {LOCALE_LABEL[locale]}{" "}
                    <span className="text-muted-foreground uppercase text-xs">({locale})</span>
                  </div>
                  {locale === "lv" && (
                    <div className="font-body text-xs text-muted-foreground">
                      {t("languages.baseLocale", "Base language — always enabled")}
                    </div>
                  )}
                </div>
                <Switch
                  checked={on}
                  disabled={locale === "lv" || loading || savingLocale === locale}
                  onCheckedChange={(v) => toggleLocale(locale as TargetLocale, v)}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Coverage ── */}
      <section className="bg-card border border-border rounded-lg p-5 space-y-4">
        <h2 className="font-display text-lg font-semibold text-foreground">
          {t("languages.coverageTitle", "Translation coverage")}
        </h2>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "languages.coverageHint",
            "Untranslated = the row has no name for that language. Click a number to see which ones.",
          )}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2 font-medium">{t("languages.colType", "Content")}</th>
                <th className="py-2 font-medium text-right">{t("languages.colTotal", "Total")}</th>
                <th className="py-2 font-medium text-right">{t("languages.colMissingEn", "EN missing")}</th>
                <th className="py-2 font-medium text-right">{t("languages.colMissingRu", "RU missing")}</th>
              </tr>
            </thead>
            <tbody>
              {CONTENT_TYPES.map((ct) => {
                const row = coverage?.content[ct.key];
                const label = t(ct.labelKey, ct.fallback);
                return (
                  <tr key={ct.key} className="border-b border-border/60">
                    <td className="py-2.5 text-foreground">{label}</td>
                    <td className="py-2.5 text-right tabular-nums text-muted-foreground">
                      {row?.total ?? "—"}
                    </td>
                    <MissingCell
                      value={row?.missing.en}
                      onClick={() => setDrill({ type: ct.key, label, locale: "en" })}
                    />
                    <MissingCell
                      value={row?.missing.ru}
                      onClick={() => setDrill({ type: ct.key, label, locale: "ru" })}
                    />
                  </tr>
                );
              })}
              {/* UI strings — override-based, no per-row drill-down. */}
              <tr>
                <td className="py-2.5 text-foreground">
                  {t("languages.uiStrings", "UI strings")}
                  <span className="block text-xs text-muted-foreground">
                    {t("languages.uiStringsHint", "Tracked dictionary keys (inline strings pending externalisation)")}
                  </span>
                </td>
                <td className="py-2.5 text-right tabular-nums text-muted-foreground">
                  {coverage?.ui.keys ?? "—"}
                </td>
                <td className="py-2.5 text-right tabular-nums text-foreground">
                  {coverage?.ui.missing.en ?? "—"}
                </td>
                <td className="py-2.5 text-right tabular-nums text-foreground">
                  {coverage?.ui.missing.ru ?? "—"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <TranslationsTable />

      {drill && (
        <UntranslatedDialog
          type={drill.type}
          label={drill.label}
          locale={drill.locale}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

function MissingCell({ value, onClick }: { value?: number; onClick: () => void }) {
  if (value == null) return <td className="py-2.5 text-right tabular-nums text-muted-foreground">—</td>;
  if (value === 0)
    return <td className="py-2.5 text-right tabular-nums text-green-600">0</td>;
  return (
    <td className="py-2.5 text-right tabular-nums">
      <button
        type="button"
        onClick={onClick}
        className="text-primary hover:underline font-medium"
      >
        {value}
      </button>
    </td>
  );
}

function UntranslatedDialog({
  type,
  label,
  locale,
  onClose,
}: {
  type: string;
  label: string;
  locale: TargetLocale;
  onClose: () => void;
}) {
  const t = useT();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<UntranslatedResp | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(0);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams({
      locale,
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (q.trim()) qs.set("q", q.trim());
    api<UntranslatedResp>(`/api/v1/admin/i18n/untranslated/${type}?${qs.toString()}`)
      .then((r) => !cancelled && setData(r))
      .catch(() => !cancelled && setData(null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [type, locale, q, page]);

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {label} — {t("languages.missingIn", "missing in")} {locale.toUpperCase()}{" "}
            <span className="text-muted-foreground font-normal">({total})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("common.search", "Search")}
            className="pl-9"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto border border-border rounded-lg divide-y divide-border">
          {loading ? (
            <div className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading...")}</div>
          ) : data && data.items.length > 0 ? (
            data.items.map((it) => (
              <div key={it.id} className="px-3 py-2 flex items-center justify-between gap-3">
                <span className="text-sm text-foreground truncate">{it.name_lv || it.slug}</span>
                <span className="text-xs text-muted-foreground shrink-0">{it.slug}</span>
              </div>
            ))
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              {t("languages.allTranslated", "Nothing untranslated here.")}
            </div>
          )}
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              {t("common.prev", "Previous")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {page + 1} / {pages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
              {t("common.next", "Next")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
